import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, User, MapPin, Heart, Calendar, MessageCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewUserProfilePage = ({ user: currentUser }) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [profileUser, setProfileUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversationStarters, setConversationStarters] = useState([]);
  const [dateIdeas, setDateIdeas] = useState([]);

  useEffect(() => {
    fetchUserProfile();
  }, [userId]);

  const fetchUserProfile = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/profile/${userId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setProfileUser(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const getConversationStarters = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const prompt = `Generate 3 conversation starters for someone interested in: ${profileUser.core_values?.join(', ')}, who is looking for ${profileUser.relationship_goals}. Format as a numbered list.`;
      
      const response = await axios.post(`${API}/ai/conversation-starters`, 
        { prompt },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Parse the response - keep full text but split by double newlines or numbered items
      const text = response.data.starters;
      const lines = text.split('\n').filter(s => s.trim());
      const starters = lines.filter(s => s.match(/^\d+[\.\)]/));
      
      setConversationStarters(starters.length > 0 ? starters : [text]);
      toast.success('Conversation starters generated!');
    } catch (error) {
      console.error('Failed to get conversation starters:', error);
      toast.error('Failed to generate conversation starters');
    }
  };

  const getDateIdeas = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const prompt = `Suggest 3-5 date ideas for someone in ${profileUser.location?.city || 'their city'} who enjoys ${profileUser.core_values?.slice(0, 3).join(', ')}. Format as a numbered list.`;
      
      const response = await axios.post(`${API}/ai/date-ideas`, 
        { prompt },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Parse the response - extract numbered items or keep full text
      const text = response.data.ideas;
      const lines = text.split('\n').filter(s => s.trim());
      const ideas = lines.filter(s => s.match(/^\d+[\.\)]/));
      
      setDateIdeas(ideas.length > 0 ? ideas : [text]);
      toast.success('Date ideas generated!');
    } catch (error) {
      console.error('Failed to get date ideas:', error);
      toast.error('Failed to generate date ideas');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="animate-pulse text-2xl font-light text-rose-600">Loading profile...</div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <Card className="shadow-lg">
          <CardContent className="p-12 text-center">
            <h3 className="text-2xl font-semibold text-rose-900 mb-2">Profile not found</h3>
            <Button onClick={() => navigate('/browse')} className="mt-4">
              Back to Browse
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Button
          data-testid="back-to-browse-btn"
          variant="ghost"
          onClick={() => navigate('/browse')}
          className="mb-6 text-rose-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Browse
        </Button>

        {/* Profile Header */}
        <Card className="shadow-xl border-rose-100 mb-6">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Profile Picture */}
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-2xl bg-gradient-to-br from-rose-200 to-amber-200 flex items-center justify-center overflow-hidden">
                  {profileUser.picture ? (
                    <img src={profileUser.picture} alt={profileUser.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-24 h-24 text-rose-400" />
                  )}
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-rose-900 mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                  {profileUser.name}
                  {profileUser.date_of_birth && `, ${new Date().getFullYear() - new Date(profileUser.date_of_birth).getFullYear()}`}
                </h1>
                
                {profileUser.location && (
                  <div className="flex items-center gap-2 text-gray-600 mb-4">
                    <MapPin className="w-4 h-4" />
                    <span>{profileUser.location.city}, {profileUser.location.country}</span>
                  </div>
                )}

                {profileUser.bio && (
                  <p className="text-gray-700 mb-4">{profileUser.bio}</p>
                )}

                <div className="flex gap-3">
                  <Button
                    data-testid="message-user-btn"
                    onClick={() => navigate('/messages')}
                    className="bg-rose-500 hover:bg-rose-600"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                  <Button
                    data-testid="get-starters-btn"
                    onClick={getConversationStarters}
                    variant="outline"
                    className="border-rose-200"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Get Conversation Starters
                  </Button>
                  <Button
                    data-testid="get-date-ideas-btn"
                    onClick={getDateIdeas}
                    variant="outline"
                    className="border-rose-200"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Get Date Ideas
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Suggestions */}
        {conversationStarters.length > 0 && (
          <Card className="shadow-lg border-amber-100 bg-gradient-to-br from-amber-50 to-rose-50 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-900">
                <Sparkles className="w-5 h-5 text-amber-600" />
                AI-Generated Conversation Starters
              </CardTitle>
              <CardDescription>Use these to break the ice!</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {conversationStarters.map((starter, index) => (
                  <li key={index} className="text-gray-700 p-3 bg-white rounded-lg border border-amber-200">
                    {starter}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {dateIdeas.length > 0 && (
          <Card className="shadow-lg border-rose-100 bg-gradient-to-br from-rose-50 to-amber-50 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-rose-900">
                <Calendar className="w-5 h-5 text-rose-600" />
                AI-Generated Date Ideas
              </CardTitle>
              <CardDescription>Perfect for your first date!</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {dateIdeas.map((idea, index) => (
                  <li key={index} className="text-gray-700 p-3 bg-white rounded-lg border border-rose-200">
                    {idea}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Relationship Goals */}
          {profileUser.relationship_goals && (
            <Card className="shadow-lg border-rose-100">
              <CardHeader>
                <CardTitle className="text-lg">Relationship Goals</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 capitalize">{profileUser.relationship_goals}</p>
              </CardContent>
            </Card>
          )}

          {/* Core Values */}
          {profileUser.core_values?.length > 0 && (
            <Card className="shadow-lg border-rose-100">
              <CardHeader>
                <CardTitle className="text-lg">Core Values</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {profileUser.core_values.map((value) => (
                    <span key={value} className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm">
                      {value}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Love Languages */}
          {profileUser.love_languages?.length > 0 && (
            <Card className="shadow-lg border-rose-100">
              <CardHeader>
                <CardTitle className="text-lg">Love Languages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {profileUser.love_languages.map((lang) => (
                    <span key={lang} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                      {lang}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Faith/Spiritual */}
          {profileUser.faith_spiritual && (
            <Card className="shadow-lg border-rose-100">
              <CardHeader>
                <CardTitle className="text-lg">Faith & Spirituality</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{profileUser.faith_spiritual}</p>
              </CardContent>
            </Card>
          )}

          {/* Lifestyle */}
          {profileUser.lifestyle_habits && Object.keys(profileUser.lifestyle_habits).length > 0 && (
            <Card className="shadow-lg border-rose-100">
              <CardHeader>
                <CardTitle className="text-lg">Lifestyle</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {profileUser.lifestyle_habits.exercise && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Exercise:</span>
                      <span className="text-gray-900 capitalize">{profileUser.lifestyle_habits.exercise}</span>
                    </div>
                  )}
                  {profileUser.lifestyle_habits.diet && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Diet:</span>
                      <span className="text-gray-900 capitalize">{profileUser.lifestyle_habits.diet}</span>
                    </div>
                  )}
                  {profileUser.lifestyle_habits.smoking && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Smoking:</span>
                      <span className="text-gray-900 capitalize">{profileUser.lifestyle_habits.smoking}</span>
                    </div>
                  )}
                  {profileUser.lifestyle_habits.drinking && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Drinking:</span>
                      <span className="text-gray-900 capitalize">{profileUser.lifestyle_habits.drinking}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Commitment & Availability */}
          {(profileUser.commitment_readiness || profileUser.emotional_availability) && (
            <Card className="shadow-lg border-rose-100">
              <CardHeader>
                <CardTitle className="text-lg">Readiness</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {profileUser.commitment_readiness && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Commitment Readiness</span>
                      <span className="text-lg font-bold text-rose-600">{profileUser.commitment_readiness}/10</span>
                    </div>
                    <Progress value={profileUser.commitment_readiness * 10} className="h-2" />
                  </div>
                )}
                {profileUser.emotional_availability && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Emotional Availability</span>
                      <span className="text-lg font-bold text-rose-600">{profileUser.emotional_availability}/10</span>
                    </div>
                    <Progress value={profileUser.emotional_availability * 10} className="h-2" />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewUserProfilePage;
